import React from 'react';
import { CustomerSourceFilter, CustomerStatusFilter } from '../../hooks/useCustomerFilters';

interface CustomerToolbarProps {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  filterSource: CustomerSourceFilter;
  setFilterSource: (value: CustomerSourceFilter) => void;
  filterStatus: CustomerStatusFilter;
  setFilterStatus: (value: CustomerStatusFilter) => void;
  filterOutstandingOnly: boolean;
  setFilterOutstandingOnly: (value: boolean) => void;
  onRefreshAllZoho: () => void;
  isRefreshingAllZoho: boolean;
  isSyncing: boolean;
  isLoadingZoho: boolean;
  onImportZoho: () => void;
  onInitialImportZoho: () => void;
  initialImportUnavailable?: boolean;
  onAdd: () => void;
}

const CustomerToolbar: React.FC<CustomerToolbarProps> = ({
  searchTerm,
  setSearchTerm,
  filterSource,
  setFilterSource,
  filterStatus,
  setFilterStatus,
  filterOutstandingOnly,
  setFilterOutstandingOnly,
  onRefreshAllZoho,
  isRefreshingAllZoho,
  isSyncing,
  isLoadingZoho,
  onImportZoho,
  onInitialImportZoho,
  initialImportUnavailable = false,
  onAdd,
}) => {
  return (
    <>
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight mb-2">Partner Ecosystem</h2>
          <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Global CRM & Commercial Registry</p>
        </div>
        <div className="flex gap-4">
          <button
            onClick={onRefreshAllZoho}
            disabled={isRefreshingAllZoho || isSyncing || isLoadingZoho}
            className={`px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl transition-all flex items-center gap-3 ${
              isRefreshingAllZoho || isSyncing || isLoadingZoho
                ? 'bg-slate-200 text-slate-400'
                : 'bg-emerald-600 text-white hover:bg-emerald-700'
            }`}
          >
            {isRefreshingAllZoho ? 'Refreshing Zoho...' : 'Refresh All Finance Data'}
          </button>
          <button
            onClick={onImportZoho}
            disabled={isSyncing || isLoadingZoho}
            className={`px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl transition-all flex items-center gap-3 ${
              isSyncing || isLoadingZoho ? 'bg-slate-200 text-slate-400' : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            {isSyncing || isLoadingZoho ? 'Loading...' : 'Import from Zoho'}
          </button>
          <button
            onClick={onInitialImportZoho}
            disabled={isSyncing || isLoadingZoho || initialImportUnavailable}
            title={initialImportUnavailable ? 'Backend update required before Initial Import All Zoho Clients is available.' : undefined}
            className={`px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl transition-all flex items-center gap-3 ${
              isSyncing || isLoadingZoho || initialImportUnavailable
                ? 'bg-slate-200 text-slate-400'
                : 'bg-amber-600 text-white hover:bg-amber-700'
            }`}
          >
            {initialImportUnavailable
              ? 'Initial Import Requires Backend Update'
              : isSyncing || isLoadingZoho
                ? 'Loading...'
                : 'Initial Import All Zoho Clients'}
          </button>
          <button
            onClick={onAdd}
            className="px-8 py-4 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl hover:bg-black transition-all"
          >
            + Onboard Legal Entity
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex flex-col lg:flex-row gap-4 lg:items-center">
        <div className="flex-1">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Search</label>
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Name, email, company, phone, Zoho ID..."
            className="mt-2 w-full bg-slate-50 border border-slate-200 rounded-xl p-3 font-bold text-sm text-slate-800 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
          />
        </div>
        <div className="flex gap-3">
          <div>
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Source</label>
            <select
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value as CustomerSourceFilter)}
              className="mt-2 bg-slate-50 border border-slate-200 rounded-xl p-3 font-bold text-sm text-slate-800"
            >
              <option value="ALL">All</option>
              <option value="ZOHO">Zoho</option>
              <option value="LOCAL">Local</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as CustomerStatusFilter)}
              className="mt-2 bg-slate-50 border border-slate-200 rounded-xl p-3 font-bold text-sm text-slate-800"
            >
              <option value="ALL">All</option>
              <option value="ACTIVE">Active</option>
              <option value="ON_HOLD">On Hold</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
              <input
                type="checkbox"
                checked={filterOutstandingOnly}
                onChange={(e) => setFilterOutstandingOnly(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300"
              />
              Outstanding Only
            </label>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomerToolbar;
